<template>
  <section
    class="FinancialAccountGroup"
    :data-collapsed-state="isOpen ? 'open' : 'closed'"
  >
    <header
      class="FinancialAccountGroup__header"
      data-testid="header"
    >
      <div class="FinancialAccountGroup__headerTitleContainer">
        <button
          v-if="accounts.length > 0"
          class="FinancialAccountGroup__headerCollapseButton"
          data-testid="collapseButton"
          @click="isOpen = !isOpen"
        >
          <Icon
            size="16px"
            class="FinancialAccountGroup__headerCollapseIcon"
            :name="IconName.ChevronDown"
          />
        </button>

        <UIText
          data-testid="title"
          tag="h3"
          size="Small"
          weight="Medium"
        >
          {{ title }}
        </UIText>
      </div>
      <UIText
        data-testid="total"
        tag="strong"
        size="Small"
        weight="Medium"
      >
        {{ formattedTotal }}
      </UIText>
    </header>

    <CollapsibleArea :isOpen="accounts.length > 0 && isOpen">
      <div class="FinancialAccountGroup__list">
        <FinancialAccountItem
          v-for="account in accounts"
          :key="account.EraAccountId"
          variant="grouped"
          :modelValue="account"
          includeSyncStatus
          @click="$emit('click:account', account)"
        />
      </div>
    </CollapsibleArea>
  </section>
</template>

<script lang="ts" setup>
import FinancialAccountItem from "@cosine/components/FinancialAccountItem.vue";
import UIText from "@cosine/components/UIText.vue";
import { IEnrichedAccountModel } from "@cosine/lib/api-models";
import formatAmount from "@cosine/lib/utils/financial/formatAmount";
import currency from "currency.js";
import { computed, ref } from "vue";
import Icon from "@cosine/components/Icon.vue";
import { IconName } from "@cosine/components/Icon.types";
import CollapsibleArea from "@cosine/components/CollapsibleArea.vue";

defineEmits<{
  "click:account": [IEnrichedAccountModel],
}>();

const {
  accounts,
} = defineProps<{
  title: string,
  accounts: Array<IEnrichedAccountModel>,
}>();

const isOpen = ref(true);

const formattedTotal = computed(() => {
  return formatAmount(accounts.reduce((acc, account) => {
    return acc.add(account.Balance);
  }, currency(0)));
});
</script>

<style lang="scss" scoped>
.FinancialAccountGroup {
  border: solid 1px var(--themeColorBorder);
  border-radius: 12px;
  background-color: var(--colorWhite);
}

.FinancialAccountGroup__header {
  position: relative;

  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
}

.FinancialAccountGroup__headerTitleContainer {
  display: flex;
  gap: 12px;
  align-items: center;
}

.FinancialAccountGroup__headerCollapseButton::after {
  position: absolute;
  inset: 0;
  content: "";
}

.FinancialAccountGroup__headerCollapseIcon {
  transition: transform 200ms ease-in-out;

  [data-collapsed-state="closed"] & {
    transform: rotate(-90deg);
  }
}

.FinancialAccountGroup__list {
  padding: 0 8px 8px;
}
</style>
